import React, { useState, useCallback, useEffect } from 'react';
import {
	Modal,
	ModalOverlay,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalBody,
	Flex,
	Input,
	Button,
	Text,
	Spinner,
	FormLabel,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import Locale from '../../../utils/translations';
import UploadAudio from '../../common/UploadAudio';
import UploadTxt from '../../common/UploadTxt';
import { BonfireAPI, ApiType } from '../../../models/BonfireApi';
import { LanguageOptionsModel } from '../../../utils/interfaces';

interface AddSampleModalProps {
	isOpen: boolean;
	onClose: () => void;
	onAddSample: (
		sampleName: string,
		audioFile?: File,
		gtFile?: File,
		languages?: string[],
	) => void;
	isCreating: boolean;
	error: string;
	setError: (msg: string) => void;
}

const AddSampleModal: React.FC<AddSampleModalProps> = ({
	isOpen,
	onClose,
	onAddSample,
	isCreating,
	error,
	setError,
}) => {
	const [sampleName, setSampleName] = useState('');
	const [audioFile, setAudioFile] = useState<File | undefined>();
	const [gtFile, setGtFile] = useState<File | undefined>();
	const [languages, setLanguages] = useState<{ value: string; label: string }[]>([]);
	const [languagesList, setLanguagesList] = React.useState<
		{ label: string; value: string }[] | []
	>([]);
	// Reset form when modal closes
	useEffect(() => {
		if (!isOpen) {
			loadLanguagesList();
			setSampleName('');
			setAudioFile(undefined);
			setGtFile(undefined);
			setLanguages([]);
			setError('');
		}
	}, [isOpen]);
	const loadLanguagesList = async () => {
		try {
			const api = new BonfireAPI(ApiType.BonfireCore);
			const data = await api.get('/languages/getSpeakingLanguages');

			const languagesOptions = data.map((language: LanguageOptionsModel) => {
				return { label: language.name, value: language.code };
			});

			setLanguagesList(languagesOptions);
		} catch (err) {
			console.error(err);
		}
	};

	const handleAdd = useCallback(() => {
		if (!sampleName.trim() || !audioFile || !gtFile || languages.length === 0) {
			setError('Please fill all the fields.');
			return;
		}
		setError('');
		onAddSample(
			sampleName,
			audioFile,
			gtFile,
			languages.map(l => l.value),
		);
	}, [sampleName, audioFile, gtFile, languages, onAddSample, setError]);

	return (
		<Modal isOpen={isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalCloseButton />
			<ModalContent bg="#F0F3F6">
				<ModalHeader>
					<Text variant="beTextHeading" fontSize="18px">
						{Locale.get('Add sample')}
					</Text>
				</ModalHeader>
				<ModalBody>
					<Flex flexDirection="column" gap={4}>
						<FormLabel>{Locale.get('Sample name')}</FormLabel>
						<Input
							bg={'white'}
							maxLength={50}
							placeholder={Locale.get('Sample name')}
							value={sampleName}
							onChange={e => setSampleName(e.target.value)}
						/>

						<UploadAudio onFileSelect={setAudioFile} />
						<UploadTxt onFileSelect={setGtFile} />

						<FormLabel>{Locale.get('Languages')}</FormLabel>
						<Select
							chakraStyles={{
								control: provided => ({
									...provided,
									_light: { backgroundColor: 'white' },
									_dark: { backgroundColor: 'black' },
									shadow: '0px 4px 4px 0px rgba(74, 74, 74, 0.04)',
								}),
								dropdownIndicator: provided => ({
									...provided,
									_light: { backgroundColor: 'white' },
									_dark: { backgroundColor: 'black' },
								}),
							}}
							isMulti
							options={languagesList}
							placeholder={Locale.get('Add languages to sample')}
							value={languages}
							onChange={val =>
								setLanguages(val as { value: string; label: string }[])
							}
							closeMenuOnSelect={false}
							maxMenuHeight={150}
						/>

						{error && (
							<Text color="red" fontSize="14px">
								{error}
							</Text>
						)}

						{!isCreating ? (
							<Button variant="bePrimary" onClick={handleAdd}>
								{Locale.get('Add')}
							</Button>
						) : (
							<Spinner />
						)}
					</Flex>
				</ModalBody>
			</ModalContent>
		</Modal>
	);
};

export default AddSampleModal;
